import * as React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { endpoint } from '..';
import { RootState } from '../../../reduxService';

import { useAppDispatch, useAppSelector } from '../../../reduxService/hooks';
import Layout from '../../components/Layout/Layout';
import './DesignCondition.scss';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import InputFormGeogrid from '../../Geogrid/components/InputForm/GeoGridForm';
import GraphicGeogrid from '../../Geogrid/components/Graphic/Graphic';
import { ButtonPanel } from '../../components/ButtonPanel';
import { IProject } from '../../api/IApiResponse';
import { requestSubmit, clearLastSubmitResult, clearSubmissionErrors, setAccordionToOpen } from '../../store/form/formControlSlice';
import { useState, useEffect } from 'react';
import { EProjectType } from '../../enum/project';
import { logDebug, logError, logWarn, LogTag } from '../../utils/utils';
import { pdfImageCaptureService } from '../../services/PdfImageCaptureService';
import { ProjectImages } from '../../types/UnifiedPdfImageTypes';
import GeogridDebugTable from '../../Geogrid/components/Results/GeogridDebugTable';
import GeogridDebugRectTable from '../../Geogrid/components/Results/GeogridDebugRectTable';
import { calculateGeogridTtot, mapGeogridDataToCalculationInputs } from '../../services/GeogridTtotService';
import { calculateRectangularGeogridTtot, mapRectangularGeogridDataToCalculationInputs } from '../../services/GeogridTtotRectService';

export const GeogridDesign = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();

    const dynamicProject = useAppSelector((state: RootState) => state.Dynamic.project?.data as IProject | undefined);
    const isFormProcessing = useAppSelector((state: RootState) => state.formControl.isSubmitting);
    const resultsData = useAppSelector((state: RootState) => state.Dynamic.results.data?.outputData);
    // Paved/Airport projects store calculation results in aashtoResults (via setAASHTOResults in useCalculationOrchestrator)
    // Railway/ContainerYard/etc store results in outputData (via setOutputData in useProjectSubmission/RailwayCalculationOrchestrator)
    const aashtoResults = useAppSelector((state: RootState) => state.Dynamic.project?.data?.aashtoResults);

    // State for GeoGrid PDF image generation
    const [geogridLayersImage, setGeogridLayersImage] = useState<string | undefined>(undefined);
    const [isGeneratingGeoGridPdfImages, setIsGeneratingGeoGridPdfImages] = useState(false);
    const [geoGridPdfError, setGeoGridPdfError] = useState<Error | null>(null);

    // State for unified project images (replacing individual states)
    const [projectImages, setProjectImages] = useState<ProjectImages>({});

    const divResults = React.useRef<HTMLDivElement>(null);

    const [showGeoGrid, setShowGeoGrid] = React.useState(false);
    const [isExecutingGeoGridRun, setIsExecutingGeoGridRun] = React.useState<boolean>(false);
    const [inResults, setInResults] = React.useState<boolean>(window.location.pathname.split('/')[1] === 'geogrid-results');

    // State for readonly mode detection
    const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);

    React.useEffect(() => {
        const onBeforeUnload = (ev: BeforeUnloadEvent) => {
            ev.returnValue = '' + t('warnings.designConditions.message');
            return '' + t('warnings.designConditions.message');
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, [t]);

    React.useEffect(() => {
        const isInResults = location.pathname.split('/')[1] === 'geogrid-results';
        setInResults(isInResults);
        setShowGeoGrid(location.pathname.split('/')[1] === 'results');
    }, [location.pathname]);

    // Detect readonly mode from sessionStorage
    useEffect(() => {
        if (!id) return;

        const viewMode = sessionStorage.getItem(`geogridViewMode_${id}`);

        if (viewMode === 'readonly') {
            setIsReadOnlyMode(true);
            logDebug('GeogridDesign', `Readonly mode enabled for project ${id}`, undefined, LogTag.NAVIGATION);
        } else {
            // Direct access - cleanup any stale flags
            try {
                sessionStorage.removeItem(`geogridViewMode_${id}`);
            } catch (error) {
                logWarn('GeogridDesign', 'Failed to cleanup stale sessionStorage flag', error, LogTag.NAVIGATION);
            }
            setIsReadOnlyMode(false);
            logDebug('GeogridDesign', `Normal editing mode for project ${id}`, undefined, LogTag.NAVIGATION);
        }

        // NO cleanup on unmount - flag persists for back/forward navigation
    }, [id]);

    const handleSave = () => {
        dispatch(requestSubmit({ source: 'ButtonSave' }));
    };

    const handleExecuteAction = () => {
        dispatch(clearLastSubmitResult());
        dispatch(clearSubmissionErrors());
        dispatch(setAccordionToOpen(null));

        if (dynamicProject?.geogridHasBeenRan) {
            navigate(endpoint.geogridResults.replace(':id', dynamicProject.guid ?? ''));
        } else if (!isExecutingGeoGridRun && !isFormProcessing) {
            setIsExecutingGeoGridRun(true);
            dispatch(requestSubmit({ source: 'StepperGeoGridRun' }));
        }
    };

    React.useEffect(() => {
        if (!isFormProcessing && isExecutingGeoGridRun) {
            setIsExecutingGeoGridRun(false);
        }
    }, [isFormProcessing, isExecutingGeoGridRun]);

    const captureGeoGridPdfImages = React.useCallback(async () => {
        setGeogridLayersImage(undefined);
        setGeoGridPdfError(null);
        setIsGeneratingGeoGridPdfImages(true);
        logDebug('GeogridDesign', 'PDF Export: Starting GeoGrid image capture with deterministic approach...', undefined, LogTag.PDF_IMAGES);

        try {
            if (!dynamicProject?.projectInfo?.data?.geoGrid) {
                logWarn('GeogridDesign', 'PDF Export: geoGrid data non disponibile, skip cattura immagini.', undefined, LogTag.PDF_IMAGES);
                setGeoGridPdfError(new Error('PDF: dati GeoGrid non disponibili'));
                setIsGeneratingGeoGridPdfImages(false);
                return;
            }

            // Add a small delay to ensure canvas is fully rendered
            // This is especially important for unpaved projects with tire images
            await new Promise(resolve => setTimeout(resolve, 500));

            // Use deterministic approach via service
            const geoGridImages = await pdfImageCaptureService.captureGeoGridImages({
                pngQuality: 0.9,
                scale: 1,
                backgroundColor: '#FFFFFF',
            });

            setGeogridLayersImage(geoGridImages.geogridLayersImage);
            logDebug(
                'GeogridDesign',
                'PDF Export: GeoGrid image captured successfully with deterministic approach',
                {
                    imageLength: geoGridImages.geogridLayersImage.length,
                },
                LogTag.PDF_IMAGES
            );
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'PDF image capture error';
            logError('GeogridDesign', 'PDF Export: Error capturing GeoGrid images:', error, LogTag.PDF_IMAGES);
            setGeoGridPdfError(new Error(t('pdf_error_image_capture') ?? errorMsg));
        } finally {
            setIsGeneratingGeoGridPdfImages(false);
        }
    }, [dynamicProject?.projectInfo?.data?.geoGrid, t]);

    useEffect(() => {
        // Trigger image capture when GeoGrid data is available (regardless of page)
        if (dynamicProject?.projectInfo?.data?.geoGrid) {
            // Immediate capture with deterministic approach - no timer needed
            captureGeoGridPdfImages();
        } else {
            setGeogridLayersImage(undefined);
            if (dynamicProject?.type === EProjectType.geogrid) {
                setGeoGridPdfError(null);
            }
        }
    }, [dynamicProject?.projectInfo?.data?.geoGrid, t, captureGeoGridPdfImages, dynamicProject?.type]);

    // Check if project type is not geogrid standalone and we have project results
    const isProjectTypeValid = dynamicProject?.type && dynamicProject.type !== EProjectType.geogrid;

    // Show Save button only for standalone geogrid projects
    // Non-geogrid projects (container_yard, paved, etc.) should not save from Geogrid page
    // to prevent data structure corruption (geogrid type overwriting project type)
    const isStandaloneGeogrid = dynamicProject?.type === EProjectType.geogrid;
    // hasProjectResults: true if EITHER outputData (Railway/etc) OR aashtoResults (Paved/Airport) exists
    const hasProjectResults = !!resultsData || !!aashtoResults;

    // For PDF button visibility, don't require images to be loaded yet - buttons can handle missing images gracefully
    // This prevents buttons from being hidden during async image loading from sessionStorage
    const hasProjectImages = true; // Always true - let buttons handle image availability internally

    // Support Combined PDF for container_yard, parking_deck, unpaved, paved, railway, and airport project types
    const supportsCombinedPdf =
        dynamicProject?.type === EProjectType.container_yard ||
        dynamicProject?.type === EProjectType.parking_deck ||
        dynamicProject?.type === EProjectType.unpaved ||
        dynamicProject?.type === EProjectType.paved ||
        dynamicProject?.type === EProjectType.railways ||
        dynamicProject?.type === EProjectType.airport;
    const shouldShowCombinedPdf = isProjectTypeValid && hasProjectResults && hasProjectImages && supportsCombinedPdf;

    // Debug logging for Combined PDF visibility
    useEffect(() => {
        if (dynamicProject?.type === EProjectType.railways || dynamicProject?.type === EProjectType.airport) {
            logDebug(
                'GeogridDesign',
                `${dynamicProject.type} Combined PDF Debug:`,
                {
                    inResults,
                    isProjectTypeValid,
                    hasProjectResults,
                    hasProjectImages,
                    supportsCombinedPdf,
                    shouldShowCombinedPdf,
                    projectType: dynamicProject?.type,
                    projectImages: Object.keys(projectImages).length > 0,
                    // Debug storage keys
                    expectedStorageKey: `${dynamicProject.type}PdfImages_${dynamicProject?.guid}`,
                    guid: dynamicProject?.guid,
                },
                LogTag.PDF_EXPORT
            );
        }
    }, [inResults, isProjectTypeValid, hasProjectResults, hasProjectImages, supportsCombinedPdf, shouldShowCombinedPdf, dynamicProject?.type, projectImages, dynamicProject?.guid]);

    // Load project images from sessionStorage using unified approach
    useEffect(() => {
        setTimeout(() => {
            if (isProjectTypeValid && hasProjectResults && supportsCombinedPdf && dynamicProject?.guid) {
                const projectType = dynamicProject.type;

                // Use the same pattern as DesignCondition: ${projectType}PdfImages_${guid}
                const storageKey = `${projectType}PdfImages_${dynamicProject.guid}`;

                try {
                    const storedImages = sessionStorage.getItem(storageKey);

                    if (storedImages) {
                        const parsed = JSON.parse(storedImages);

                        // Set unified project images
                        setProjectImages({
                            ...parsed,
                            captureTimestamp: parsed.captureTimestamp || Date.now(),
                            captureMethod: parsed.captureMethod || 'legacy',
                        });

                        logDebug('GeogridDesign', `Successfully loaded ${projectType} images from storage: ${storageKey}`, undefined, LogTag.PDF_IMAGES);
                    } else {
                        logWarn('GeogridDesign', `No stored images found for ${projectType} - please visit the results page first`, { storageKey }, LogTag.PDF_IMAGES);
                    }
                } catch (storageError) {
                    logError('GeogridDesign', 'Error loading project images from storage', storageError, LogTag.PDF_IMAGES);
                }
            }
        }, 1000);
    }, [isProjectTypeValid, hasProjectResults, supportsCombinedPdf, dynamicProject?.guid, dynamicProject?.type]);

    // Combined PDF image capture logic
    useEffect(() => {
        if (shouldShowCombinedPdf) {
            // PDF images are ready for display
        }
    }, [shouldShowCombinedPdf]);

    return (
        <Layout>
            <Grid className="design-condition" container>
                <Grid className="form-area" item xs={12} md={3} lg={3}>
                    <InputFormGeogrid />
                </Grid>
                <Grid className="canvas-area" item xs={12} md={9} lg={9}>
                    <ButtonPanel
                        projectType={dynamicProject?.type || EProjectType.container_yard}
                        showSave={isStandaloneGeogrid}
                        showExportGeoGridPdf={inResults && !!dynamicProject?.projectInfo?.data?.geoGrid}
                        showExportCombinedPdf={inResults && shouldShowCombinedPdf}
                        showGeoGrid={showGeoGrid}
                        showExecute={!inResults}
                        onSave={handleSave}
                        onExecute={handleExecuteAction}
                        saveDisabled={isFormProcessing}
                        isReadOnly={isReadOnlyMode}
                        // Use unified props system
                        useUnifiedProps={true}
                        projectImages={projectImages}
                        isGeneratingPdfImages={false}
                        pdfImageError={null}
                        // Pass GeoGrid PDF image data and states
                        geoGridLayersImage={geogridLayersImage}
                        isGeneratingGeoGridPdfImages={isGeneratingGeoGridPdfImages}
                        geoGridPdfError={geoGridPdfError}
                    />
                    <Grid item xs={12} ref={divResults}>
                        <GraphicGeogrid horizontalSpacing={10} verticalSpacing={10} />
                        {/* Debug Table - HIDDEN for production */}
                        {/* {inResults && dynamicProject?.projectInfo?.data?.geoGrid && (() => {
                            try {
                                // Map geogrid data to calculation inputs
                                const geogridData = dynamicProject.projectInfo.data.geoGrid;
                                const geometryType = geogridData.type?.geogridType;

                                logDebug('GeogridDesign', 'Debug table rendering', {
                                    geometryType,
                                    isRectangular: geometryType === 'Rectangular',
                                    isCircular: geometryType === 'Circular'
                                }, LogTag.GEOGRID_CALC);

                                // Render appropriate debug table based on geometry type
                                if (geometryType === 'Rectangular' || geometryType === 'Railway') {
                                    // RECTANGULAR GEOMETRY PATH (includes Railway)
                                    const rectangularInputs = mapRectangularGeogridDataToCalculationInputs(geogridData, 0, 'subbase');

                                    if (!rectangularInputs) {
                                        logWarn('GeogridDesign', 'No valid rectangular calculation inputs from geogrid data', undefined, LogTag.GEOGRID_CALC);
                                        return null;
                                    }

                                    // Calculate rectangular geogrid Ttot for subbase (layer 1)
                                    const rectangularResult = calculateRectangularGeogridTtot(rectangularInputs, 0, 'subbase');

                                    if (!rectangularResult.designTable) {
                                        logWarn('GeogridDesign', 'No design table in rectangular result', undefined, LogTag.GEOGRID_CALC);
                                        return null;
                                    }

                                    logDebug('GeogridDesign', 'Rendering GeogridDebugRectTable', {
                                        resultValue: rectangularResult.value,
                                        resultStatus: rectangularResult.status
                                    }, LogTag.GEOGRID_CALC);

                                    return (
                                        <GeogridDebugRectTable
                                            inputs={rectangularInputs}
                                            designTable={rectangularResult.designTable}
                                        />
                                    );
                                } else {
                                    // CIRCULAR GEOMETRY PATH (default)
                                    const circularInputs = mapGeogridDataToCalculationInputs(geogridData, 0, 'subbase');

                                    if (!circularInputs) {
                                        logWarn('GeogridDesign', 'No valid circular calculation inputs from geogrid data', undefined, LogTag.GEOGRID_CALC);
                                        return null;
                                    }

                                    // Calculate circular geogrid Ttot for subbase (layer 1)
                                    const circularResult = calculateGeogridTtot(circularInputs, 0, 'subbase');

                                    logDebug('GeogridDesign', 'Rendering GeogridDebugTable (circular)', {
                                        resultValue: circularResult.value,
                                        resultStatus: circularResult.status
                                    }, LogTag.GEOGRID_CALC);

                                    return (
                                        <GeogridDebugTable
                                            result={circularResult}
                                            inputs={circularInputs}
                                        />
                                    );
                                }
                            } catch (error) {
                                logError('GeogridDesign', 'Error calculating debug table data:', error, LogTag.GEOGRID_CALC);
                                return null;
                            }
                        })()} */}
                    </Grid>
                </Grid>
            </Grid>
        </Layout>
    );
};
